@if (isLoading()) {
  <div class="sav-dh-wrap">
    <div class="sav-dh-skel sav-dh-skel-pill"></div>
    <div class="sav-dh-skel sav-dh-skel-title"></div>
    <div class="sav-dh-skel sav-dh-skel-line"></div>
    <div class="sav-dh-skel sav-dh-skel-grid"></div>
  </div>
} @else if (notFound()) {
  <div class="sav-dh-empty-page">
    <h1>Page not found</h1>
    <p>This discount hub is not available.</p>
    <a routerLink="/storelist" class="sav-dh-btn">Browse all stores</a>
  </div>
} @else if (hub(); as data) {
  <div class="sav-dh-wrap">
    <app-breadcrumb [items]="crumbs()" />

    <!-- Hero -->
    <section class="sav-dh-hero" aria-label="Overview">
      @if (lastUpdated()) {
        <span class="sav-dh-status">
          <span class="sav-dh-dot" aria-hidden="true"></span>
          {{ allStores().length }} store{{ allStores().length === 1 ? '' : 's' }} listed · updated
          {{ lastUpdated() }}
        </span>
      }

      <h1>{{ heading() }}</h1>
      <p class="sav-dh-sub">{{ intro() }}</p>

      <div class="sav-dh-search">
        <input
          type="search"
          class="sav-dh-search-input"
          placeholder="Search stores"
          aria-label="Search stores"
          [value]="search()"
          (input)="onSearch($any($event.target).value)"
        />
        <span class="sav-dh-search-hint" aria-hidden="true">{{ allStores().length }} stores</span>
      </div>
    </section>

    @if (highlights().length > 0) {
      <section class="sav-dh-highlights" aria-label="Highlights">
        @for (card of highlights(); track card.slug + card.eyebrow) {
          <a class="sav-dh-highlight" [routerLink]="'/' + card.slug">
            <span class="sav-dh-highlight-badge">{{ card.badge }}</span>
            <span class="sav-dh-highlight-text">
              <span class="sav-dh-eyebrow">{{ card.eyebrow }}</span>
              <span class="sav-dh-highlight-title">{{ card.title }}</span>
            </span>
            <span class="sav-dh-arrow" aria-hidden="true">→</span>
          </a>
        }
      </section>
    }

    <!-- Store listing -->
    <section id="stores" class="sav-dh-section" aria-labelledby="dh-stores-heading">
      <div class="sav-dh-section-head">
        <h2 id="dh-stores-heading">{{ data.label }} by store</h2>
        <p class="sav-dh-count" aria-live="polite">{{ resultLabel() }}</p>
      </div>

      @if (categories().length > 0) {
        <div class="sav-dh-chips" role="group" aria-label="Filter by category">
          <button
            type="button"
            class="sav-dh-chip"
            [class.is-active]="activeCategory() === ''"
            [attr.aria-pressed]="activeCategory() === ''"
            (click)="selectCategory('')"
          >
            All · {{ allStores().length }}
          </button>
          @for (category of categories(); track category.slug) {
            <button
              type="button"
              class="sav-dh-chip"
              [class.is-active]="activeCategory() === category.slug"
              [attr.aria-pressed]="activeCategory() === category.slug"
              (click)="selectCategory(category.slug)"
            >
              {{ category.name }} · {{ category.count }}
            </button>
          }
        </div>
      }

      <div class="sav-dh-sorts" role="group" aria-label="Sort stores">
        <span class="sav-dh-sort-label">Sort</span>
        <button
          type="button"
          class="sav-dh-sort"
          [class.is-active]="sortKey() === 'popular'"
          (click)="setSort('popular')"
        >
          Most used
        </button>
        <button
          type="button"
          class="sav-dh-sort"
          [class.is-active]="sortKey() === 'discount'"
          (click)="setSort('discount')"
        >
          Biggest discount
        </button>
        <button
          type="button"
          class="sav-dh-sort"
          [class.is-active]="sortKey() === 'name'"
          (click)="setSort('name')"
        >
          A–Z
        </button>
      </div>

      @if (visibleStores().length > 0) {
        <div class="sav-dh-grid">
          @for (store of visibleStores(); track store.slug) {
            <article class="sav-dh-card">
              <div class="sav-dh-card-body">
                <div class="sav-dh-card-head">
                  <span class="sav-dh-logo">
                    @if (store.storeLogoImagePath) {
                      <img
                        [src]="store.storeLogoImagePath | assetUrl"
                        [alt]="store.storeName"
                        loading="lazy"
                      />
                    } @else {
                      {{ store.storeName.charAt(0) }}
                    }
                  </span>
                  <span class="sav-dh-card-id">
                    <span class="sav-dh-card-name">{{ store.storeName }}</span>
                    @if (store.categories.length > 0) {
                      <span class="sav-dh-card-cat">{{ store.categories[0].name }}</span>
                    }
                  </span>
                </div>

                <!-- Badges 1 and 2 of the assigned coupon, exactly as the store-page card shows
                     them. -->
                <p class="sav-dh-offer">
                  <span
                    class="sav-dh-offer-value"
                    [class.is-long]="offerPrimary(store).length > 4"
                    >{{ offerPrimary(store) }}</span
                  >
                  @if (offerSecondary(store)) {
                    <span class="sav-dh-offer-unit">{{ offerSecondary(store) }}</span>
                  }
                </p>

                @if (store.shortDescription) {
                  <div
                    class="sav-dh-card-desc"
                    [innerHTML]="store.shortDescription | trustedCmsHtml"
                  ></div>
                }

                <p class="sav-dh-card-meta">
                  @if (offerTypeLabel(store)) {
                    <span class="sav-dh-type">{{ offerTypeLabel(store) }}</span>
                  }
                  <span>{{ offerMeta(store) }}</span>
                </p>
              </div>

              <a class="sav-dh-card-link" [routerLink]="'/' + store.slug">
                View {{ store.storeName }} {{ labelLower() }} <span aria-hidden="true">→</span>
              </a>
            </article>
          }
        </div>
      } @else {
        <div class="sav-dh-empty">
          No stores match that search. Try a different name, or clear the category filter.
        </div>
      }
    </section>

    @if (data.about) {
      <section class="sav-dh-section sav-dh-about" aria-labelledby="dh-about-heading">
        <h2 id="dh-about-heading">About {{ labelLower() }}s</h2>
        <div class="sav-dh-prose" [innerHTML]="data.about | trustedCmsHtml"></div>
      </section>
    }
  </div>
}
